// MeterTabs.tsx - Updated to use separate components for each tab
import React, { useState, useEffect } from "react";
import { Wifi, Camera, Settings, Gauge } from "lucide-react";
import { useMeterModels } from "../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Tabs } from "@shared/ui/tabs";
import { useLocation } from "react-router-dom";
import { PAGE_SIZE } from "@shared/utils/constants";
import { AllMeterModels } from "./tabs/allMetersModel";
import { SmartMeterModels } from "./tabs/smartMeterModels";
import { PhotoMeterModels } from "./tabs/photoMeterModels";
import { ManualMeterModels } from "./tabs/manualMeterModels";

interface MeterTabsProps {
  onDelete: (id: string) => void;
  loading?: boolean;
  searchValue?: string;
}

export const MeterTabs: React.FC<MeterTabsProps> = ({
  onDelete,
  loading = false,
  searchValue = "",
}) => {
  const remoteUtilityId = getRemoteUtilityId();
  const location = useLocation();

  // Get current tab from URL or default to "all"
  const getCurrentTab = (): string => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get("tab");
    return tab || "all";
  };

  const [activeTab, setActiveTab] = useState<string>(getCurrentTab());

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getCurrentTab());
  }, [location.search]);

  // Fetch data for all tabs to get counts
  const { data: allMetersData, isLoading: allMetersLoading } = useMeterModels({
    remote_utility_id: remoteUtilityId,
    meter_type: "", // All meters
    searchData: "",
    manufacturer: "",
    is_active: "",
    page: 1,
    limit: PAGE_SIZE,
  });

  const { data: smartMetersData, isLoading: smartMetersLoading } = useMeterModels({
    remote_utility_id: remoteUtilityId,
    meter_type: 1, // Smart meters
    searchData: "",
    manufacturer: "",
    is_active: "",
    page: 1,
    limit: PAGE_SIZE,
  });

  const { data: photoMetersData, isLoading: photoMetersLoading } = useMeterModels({
    remote_utility_id: remoteUtilityId,
    meter_type: 0, // Photo meters
    searchData: "",
    manufacturer: "",
    is_active: "",
    page: 1,
    limit: PAGE_SIZE,
  });

  const { data: manualMetersData, isLoading: manualMetersLoading } = useMeterModels({
    remote_utility_id: remoteUtilityId,
    meter_type: 4, // Manual meters
    searchData: "",
    manufacturer: "",
    is_active: "",
    page: 1,
    limit: PAGE_SIZE,
  });

  // Get counts for each tab type
  const getCounts = () => {
    return {
      all: allMetersData?.count || 0,
      smart: smartMetersData?.count || 0,
      photo: photoMetersData?.count || 0,
      manual: manualMetersData?.count || 0,
    };
  };

  const counts = getCounts();

  // Check if any data is still loading
  const isLoadingCounts = allMetersLoading || smartMetersLoading || photoMetersLoading || manualMetersLoading;

  // Tab configuration using the Tabs service format
  const tabComponents = {
    all: {
      label: `All Meters Manufactureres ${isLoadingCounts ? "" : `(${counts.all})`}`,
      shortLabel: `All ${isLoadingCounts ? "" : `(${counts.all})`}`,
      icon: <Gauge className="h-4 w-4" />,
      component: <AllMeterModels onDelete={onDelete} />,
    },
    smart: {
      label: `Smart Meters Manufacturer ${isLoadingCounts ? "" : `(${counts.smart})`}`,
      shortLabel: `Smart ${isLoadingCounts ? "" : `(${counts.smart})`}`,
      icon: <Wifi className="h-4 w-4" />,
      component: <SmartMeterModels onDelete={onDelete} />,
    },
    photo: {
      label: `Photo Meters Manufacturer ${isLoadingCounts ? "" : `(${counts.photo})`}`,
      shortLabel: `Photo ${isLoadingCounts ? "" : `(${counts.photo})`}`,
      icon: <Camera className="h-4 w-4" />,
      component: <PhotoMeterModels onDelete={onDelete} />,
    },
    manual: {
      label: `Manual Meters Manufacturer ${isLoadingCounts ? "" : `(${counts.manual})`}`,
      shortLabel: `Manual ${isLoadingCounts ? "" : `(${counts.manual})`}`,
      icon: <Settings className="h-4 w-4" />,
      component: <ManualMeterModels onDelete={onDelete} />,
    },
  };

  const urlMapping = {
    all: "all",
    smart: "smart",
    photo: "photo",
    manual: "manual",
  };

  // Custom loading component for tabs
  const tabLoadingComponent = (
    <div className="space-y-4">
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded mb-2"></div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <Tabs
        defaultValue="all"
        tabComponents={tabComponents}
        urlMapping={urlMapping}
        tabsListClassName="grid w-full grid-cols-4 mb-6"
        defaultLoadingComponent={tabLoadingComponent}
        idPrefix="meter-models-tab"
        level={0}
      />
    </div>
  );
};